import { useEffect, useRef, useState } from "react"
import { format } from "date-fns"
import { toast } from "react-toastify"
import { CallDataSourceDto } from "@dialogics/shared/dto/types"
import { IngestionStatusName } from "@dialogics/shared/dto/temporal"
import { useStartTranscriptionMutation, useTranscriptionStatusQuery } from "./service"
import { INGESTION_STATUS_VIEW } from "./ingestion-utils"
import { DEFAULT_TRANSCRIPTION_LIMIT, MAX_TRANSCRIPTION_LIMIT, MIN_TRANSCRIPTION_LIMIT, parseTranscriptionLimit } from "./transcription-utils"

const RUN_POLLING_INTERVAL_MS = 2000
const PROGRESS_POLLING_INTERVAL_MS = 10000
const DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm:ss"

const formatDateTime = (value?: string) => (value ? format(new Date(value), DATE_TIME_FORMAT) : "—")

const getErrorMessage = (e: unknown) =>
  (e as { data?: { message?: string } })?.data?.message ?? "Ошибка при запуске транскрибации"

export function RunTranscriptionModal({ dataSource, onClose }: { dataSource: CallDataSourceDto; onClose: () => void }) {
  const [limitInput, setLimitInput] = useState(String(DEFAULT_TRANSCRIPTION_LIMIT))
  const [pollingInterval, setPollingInterval] = useState(0)
  const { data, isLoading } = useTranscriptionStatusQuery(dataSource.id, { pollingInterval })
  const [startTranscription, { isLoading: isStarting }] = useStartTranscriptionMutation()

  const run = data?.run ?? null
  const pendingCount = data?.pendingCount ?? 0
  const inProgressCount = data?.inProgressCount ?? 0
  const status = run?.status
  const isRunning = status === "RUNNING"
  const view = status ? INGESTION_STATUS_VIEW[status] : undefined
  const limit = parseTranscriptionLimit(limitInput)
  const canStart = !isRunning && !isStarting && !isLoading && limit !== null && pendingCount > 0

  // Poll fast while the run queues files, then slowly while queued files are being transcribed
  useEffect(() => {
    setPollingInterval(isRunning ? RUN_POLLING_INTERVAL_MS : inProgressCount > 0 ? PROGRESS_POLLING_INTERVAL_MS : 0)
  }, [isRunning, inProgressCount])

  // Notify only on a RUNNING → final transition, not for a historical result shown on open
  const previousStatus = useRef<IngestionStatusName | undefined>(undefined)
  useEffect(() => {
    if (previousStatus.current === "RUNNING" && status && status !== "RUNNING") {
      if (status === "COMPLETED") {
        toast.success(`Транскрибация: поставлено в очередь ${run?.result?.queued ?? 0} файлов`)
        if (run?.result?.skippedNoBalance) {
          toast.warning(`Пропущено из-за недостаточного баланса: ${run.result.skippedNoBalance}`)
        }
      } else {
        toast.error(`Транскрибация: ${view?.label ?? status}${run?.error ? ` — ${run.error}` : ""}`)
      }
    }
    previousStatus.current = status
  }, [status, view, run?.error, run?.result])

  const handleStart = async () => {
    if (limit === null) return
    try {
      await startTranscription({ id: dataSource.id, limit }).unwrap()
      toast.info("Транскрибация запущена")
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  return (
    <dialog className="modal" open>
      <div className="modal-box w-11/12 max-w-lg">
        <h2 className="text-xl font-bold">Транскрибация звонков</h2>
        <p className="text-sm opacity-70 mt-1">
          Источник #{dataSource.id} · {dataSource.type}
        </p>

        <div className="stats stats-horizontal w-full mt-4 bg-base-200">
          <div className="stat py-2">
            <div className="stat-title">Ожидают транскрибации</div>
            <div className="stat-value text-2xl" data-testid="pending-count">
              {isLoading ? "…" : pendingCount}
            </div>
          </div>
          <div className="stat py-2">
            <div className="stat-title">В обработке</div>
            <div className="stat-value text-2xl" data-testid="in-progress-count">
              {isLoading ? "…" : inProgressCount}
            </div>
          </div>
        </div>

        <fieldset className="fieldset mt-2">
          <legend className="fieldset-legend">Максимум файлов за запуск</legend>
          <input
            type="number"
            className="input w-full"
            min={MIN_TRANSCRIPTION_LIMIT}
            max={MAX_TRANSCRIPTION_LIMIT}
            step={1}
            value={limitInput}
            onChange={e => setLimitInput(e.target.value)}
            disabled={isRunning}
          />
          <p className="label">
            {limit === null
              ? `Укажите целое число от ${MIN_TRANSCRIPTION_LIMIT} до ${MAX_TRANSCRIPTION_LIMIT}`
              : "Сначала берутся самые старые звонки"}
          </p>
        </fieldset>

        {run && (
          <fieldset className="fieldset mt-2">
            <legend className="fieldset-legend">Последний запуск</legend>
            <div className="flex items-center gap-2">
              <span className={`badge ${view?.badge ?? "badge-ghost"}`} data-testid="transcription-status">
                {isRunning && <span className="loading loading-spinner loading-xs" />}
                {view?.label ?? status}
              </span>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-2">
              <dt className="opacity-70">Запущено</dt>
              <dd>{formatDateTime(run.startTime)}</dd>
              {run.closeTime && (
                <>
                  <dt className="opacity-70">Завершено</dt>
                  <dd>{formatDateTime(run.closeTime)}</dd>
                </>
              )}
              {run.result && (
                <>
                  <dt className="opacity-70">Найдено файлов</dt>
                  <dd data-testid="result-found">{run.result.found}</dd>
                  <dt className="opacity-70">Поставлено в очередь</dt>
                  <dd data-testid="result-queued">{run.result.queued}</dd>
                  <dt className="opacity-70">Пропущено (баланс)</dt>
                  <dd data-testid="result-skipped">{run.result.skippedNoBalance}</dd>
                </>
              )}
            </dl>
            {run.error && <div className="alert alert-error alert-soft text-sm mt-2">{run.error}</div>}
          </fieldset>
        )}

        <div className="modal-action">
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
          <button type="button" className="btn btn-primary" onClick={handleStart} disabled={!canStart}>
            {isStarting && <span className="loading loading-spinner loading-sm" />}
            Запустить
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop" onClick={onClose}>
        <button>закрыть</button>
      </form>
    </dialog>
  )
}
